import { useState } from 'react'
import { ActivityIndicator, Platform, Pressable, Text, View } from 'react-native'

import { BaseModal } from '../../ui/BaseModal'
import { PrivacyLegalDocumentModal } from '../PrivacyLegalDocumentModal/PrivacyLegalDocumentModal'
import { useLegalReconsent } from '../../../src/privacy/hooks/useLegalReconsent'
import { legalReconsentGateStyles as styles } from './LegalReconsentGate.styles'
import { theme } from '../../../theme'
import type { InAppLegalDocumentVariant } from '../../../src/landing/types/legalDocument.types'

export interface LegalReconsentGateProps {
  testID?: string
}

/**
 * Asks a signed-in user to re-accept the Terms when the published version has changed.
 *
 * Mounted in the app shell only, so it can never appear over the public marketing site.
 * Guests are never prompted. Declining signs the user out — there is no "later", because
 * the recorded consent would otherwise keep pointing at a document that no longer applies.
 */
export function LegalReconsentGate({ testID }: LegalReconsentGateProps) {
  const { isPromptVisible, isSubmitting, hasError, accept, decline } = useLegalReconsent()
  const [openDocument, setOpenDocument] = useState<InAppLegalDocumentVariant | null>(null)

  if (!isPromptVisible) return null

  return (
    <>
      <BaseModal
        isVisible
        // No dismiss: the only ways out are Accept or Decline.
        onDismiss={() => undefined}
        contentAlignment="center"
        contentContainerStyle={styles.modalOuter}
        testID={testID ?? 'legal-reconsent-gate'}
        contentTestID={testID ? `${testID}-content` : 'legal-reconsent-gate-content'}
        // iOS: an in-app layer, so a sign-in sheet or switcher still closing cannot block it (AC-5.5).
        renderInline={Platform.OS === 'ios'}
      >
        <View style={styles.sheet}>
          <Text style={styles.title}>We&apos;ve updated our Terms</Text>
          <Text style={styles.body}>
            Our Terms of Service and Privacy Policy have changed. Please review and accept
            them to keep using Zporti.
          </Text>

          <View style={styles.linkRow}>
            <Pressable
              accessibilityRole="link"
              style={styles.link}
              onPress={() => setOpenDocument('terms')}
              testID="legal-reconsent-gate-open-terms"
            >
              <Text style={styles.linkText}>Terms of Service</Text>
            </Pressable>
            <Pressable
              accessibilityRole="link"
              style={styles.link}
              onPress={() => setOpenDocument('privacy')}
              testID="legal-reconsent-gate-open-privacy"
            >
              <Text style={styles.linkText}>Privacy Policy</Text>
            </Pressable>
          </View>

          {hasError ? (
            <Text style={styles.error}>
              We couldn&apos;t save that. Please check your connection and try again.
            </Text>
          ) : null}

          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              style={styles.declineButton}
              disabled={isSubmitting}
              onPress={() => void decline()}
              testID="legal-reconsent-gate-decline"
            >
              <Text style={styles.declineText}>Decline and sign out</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              style={[styles.acceptButton, isSubmitting && styles.acceptButtonDisabled]}
              disabled={isSubmitting}
              onPress={() => void accept()}
              testID="legal-reconsent-gate-accept"
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={theme.white} />
              ) : (
                <Text style={styles.acceptText}>Accept</Text>
              )}
            </Pressable>
          </View>
        </View>
      </BaseModal>

      <PrivacyLegalDocumentModal
        variant={openDocument}
        isVisible={openDocument !== null}
        onDismiss={() => setOpenDocument(null)}
      />
    </>
  )
}
